import React, { useState } from "react";
import Modal from "../Modal/Modal";
import Icon from "../Icon/Icon";
import { useContrattoTesti } from "../../hooks/useContratto";
import { URL_PRIVACY } from "../../config/app";
import s from "./Contratto.module.scss";

/**
 * Contratto di Payroll Manager: Condizioni Generali, Allegati e Informativa
 * Privacy, nei testi pubblicati dal backend (GET /api/public/contratto).
 * Qui non si compone mai un testo: si mostra quello di cui il backend
 * registra l'hash alla firma.
 */

/** Un documento legale, dai blocchi preparati dal backend (legal/struttura.js). */
export function DocumentoLegale({ blocchi }) {
  return (
    <div className={s.lettore}>
      {(blocchi || []).map((b, i) => {
        switch (b.tipo) {
          case "titolo":
            return null;
          case "sottotitolo":
            return (
              <p key={i} className={s.sottotitolo}>
                {b.testo}
              </p>
            );
          case "sezione":
            return (
              <h3 key={i} id={b.id}>
                {b.testo}
              </h3>
            );
          case "sottosezione":
            return <h4 key={i}>{b.testo}</h4>;
          case "elenco":
            return (
              <ul key={i}>
                {b.voci.map((v, j) => (
                  <li key={j}>{v}</li>
                ))}
              </ul>
            );
          case "tabella":
            return (
              <div key={i} className={s.tabella}>
                <table>
                  <thead>
                    <tr>
                      {b.righe[0].map((c, j) => (
                        <th key={j}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.righe.slice(1).map((r, j) => (
                      <tr key={j}>
                        {r.map((c, k) => (
                          <td key={k}>{c}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "firma":
            return (
              <div key={i} className={s.firma}>
                {b.voci.map((v, j) => (
                  <p key={j}>{v}</p>
                ))}
              </div>
            );
          default:
            return (
              <p key={i}>
                {b.etichetta ? (
                  <>
                    <strong>{b.etichetta}:</strong> {b.resto}
                  </>
                ) : (
                  b.testo
                )}
              </p>
            );
        }
      })}
    </div>
  );
}

export function DocumentoModal({ documento, onClose }) {
  return (
    <Modal
      title={documento.titolo}
      subtitle={documento.versione ? `Versione ${documento.versione}` : undefined}
      onClose={onClose}
      size="lg"
      footer={
        <button type="button" className="pr-btn pr-btn-confirm" onClick={onClose}>
          Ho letto
        </button>
      }
    >
      <DocumentoLegale blocchi={documento.blocchi} />
    </Modal>
  );
}

/** I documenti del contratto (e l'informativa) come pulsanti che aprono il lettore. */
export function ElencoDocumenti({ testi, onApri }) {
  return (
    <div className={s.docs}>
      {testi.documenti.map((d) => (
        <button key={d.tipo} type="button" className={s.doc} onClick={() => onApri({ ...d, versione: testi.versione })}>
          <Icon name="file" size={18} />
          <span>{d.titolo}</span>
          <Icon name="chevronRight" size={16} />
        </button>
      ))}
    </div>
  );
}

/**
 * Presa visione dell'Informativa Privacy, l'unico consenso che si dà creando
 * un account (registrazione e invito): il contratto si firma all'attivazione
 * del piano. L'informativa si legge senza uscire dalla pagina; se il backend
 * non la dà, si apre quella pubblicata sul sito.
 */
export function ConsensoPrivacy({ checked, onChange, className }) {
  const testi = useContrattoTesti();
  const [aperta, setAperta] = useState(false);
  return (
    <>
      <label className={className}>
        <input type="checkbox" className="pr-check" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>
          Dichiaro di aver letto l'
          <button
            type="button"
            className="pr-link-btn"
            onClick={(e) => {
              e.preventDefault();
              if (testi.data) setAperta(true);
              else window.open(URL_PRIVACY, "_blank", "noopener");
            }}
          >
            Informativa Privacy
          </button>{" "}
          *
        </span>
      </label>
      {aperta && testi.data && <DocumentoModal documento={testi.data.privacy} onClose={() => setAperta(false)} />}
    </>
  );
}

/**
 * Lettura dei documenti e i tre consensi distinti dell'art. 22: Condizioni e
 * Allegati, approvazione specifica delle clausole (artt. 1341-1342 c.c., con
 * l'elenco accanto alla casella), presa visione dell'Informativa Privacy.
 */
export function ConsensiContratto({ testi, consensi, onChange }) {
  const [aperto, setAperto] = useState(null);
  const set = (k) => (e) => onChange({ ...consensi, [k]: e.target.checked });
  const privacy = testi.privacy;

  return (
    <>
      <ElencoDocumenti testi={testi} onApri={setAperto} />
      <div className={s.consensi}>
        <label className={s.consenso}>
          <input type="checkbox" className="pr-check" checked={consensi.contratto} onChange={set("contratto")} />
          <span>
            Ho letto e accetto le <strong>Condizioni Generali di Servizio</strong> e gli Allegati Tecnico, Economico, di Assistenza e sul
            Trattamento dei Dati Personali *
          </span>
        </label>
        <label className={s.consenso}>
          <input type="checkbox" className="pr-check" checked={consensi.clausole} onChange={set("clausole")} />
          <span>
            Approvo specificamente, ai sensi degli <strong>artt. 1341 e 1342 c.c.</strong>, le clausole indicate qui sotto *
            {testi.clausole && <span className={s.clausole}>{testi.clausole}</span>}
          </span>
        </label>
        <label className={s.consenso}>
          <input type="checkbox" className="pr-check" checked={consensi.privacy} onChange={set("privacy")} />
          <span>
            Dichiaro di aver letto l'
            <button
              type="button"
              className="pr-link-btn"
              onClick={(e) => {
                e.preventDefault();
                setAperto(privacy);
              }}
            >
              Informativa Privacy
            </button>{" "}
            *
          </span>
        </label>
      </div>
      {aperto && <DocumentoModal documento={aperto} onClose={() => setAperto(null)} />}
    </>
  );
}
